import currency from 'currency.js';
import React, { useMemo } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormHandleSubmit,
  UseFormSetValue,
  useFormContext,
} from 'react-hook-form';

import {
  DEFAULT_MAX_TRIP_COST_PER_PERSON,
  DEFAULT_MIN_TRIP_COST_PER_PERSON,
} from '@packages/constants';
import { useConfigStore } from '@packages/stores';
import { TripCostForm } from '@packages/types';
import { getCurrencySymbol } from '@packages/utils';

import { Button } from '@elements/button';
import { Input } from '@elements/input';
import { Text } from '@elements/text';
import { i18n } from '@i18n';
import { getTripSelector, useTripStore } from '@store';
import { sendAnalyticsEvent, testProps } from '@utils';

const formatFormTripCost = (value: number | null | undefined, prefixValue: string) => {
  if (value == null || isNaN(value)) return '';
  return (
    prefixValue +
    currency(value, {
      symbol: '',
      precision: 0,
      separator: ',',
    }).format()
  );
};

const onSubmit = (
  value: string,
  handleSubmit: UseFormHandleSubmit<TripCostForm, undefined>,
  prefixValue: string,
  setValue: UseFormSetValue<TripCostForm>,
) => {
  const formTripCost = parseInt(value.replace(prefixValue ?? '', '').replace(/,/g, ''), 10);
  const currentTotalTripCost = useTripStore.getState().trip?.totalTripCost;
  if (formTripCost === Number(currentTotalTripCost)) {
    return;
  }

  handleSubmit(async (values: TripCostForm) => {
    const totalTripCost = parseInt(
      values.totalTripCost.replace(prefixValue ?? '', '').replace(/,/g, ''),
      10,
    );
    if (!totalTripCost) {
      return;
    }
    const prevTotalTripCost = Number(useTripStore.getState().trip?.totalTripCost);
    useTripStore.getState().setTotalTripCost(totalTripCost);
    sendAnalyticsEvent('Quote Submitted');
    const trip = await useTripStore.getState().syncTrip({ quiet: true });
    if (!trip) {
      setValue('totalTripCost', formatFormTripCost(prevTotalTripCost, prefixValue));
    }
  })();
};

export const TripCostInput = () => {
  const { control } = useFormContext<TripCostForm>();
  const isPending = useTripStore(state => state.pending);
  const isBoostUpdating = useTripStore(state =>
    Object.values(state.boostApiState).some(i => i === 'pending'),
  );

  const {
    setValue,
    formState: { errors },
    handleSubmit,
  } = useFormContext<TripCostForm>();

  const regionSpecificConfig = useConfigStore(state => state?.regionSpecificConfig);

  const prefixValue: string | undefined = useMemo(
    () => getCurrencySymbol(regionSpecificConfig),
    [regionSpecificConfig],
  );

  const trip = useTripStore(getTripSelector);

  const selectedSecondaryTravellerCount =
    trip?.secondaryTravellers.filter(t => t.isSelected).length ?? 0;
  const minTripCostPerPerson =
    regionSpecificConfig?.RULES.TRIP_COST_MIN_PER_PERSON ?? DEFAULT_MIN_TRIP_COST_PER_PERSON;
  const tripCostMin = React.useMemo(
    () => minTripCostPerPerson * (selectedSecondaryTravellerCount + 1),
    [minTripCostPerPerson, selectedSecondaryTravellerCount],
  );
  const maxTripCostPerPerson =
    regionSpecificConfig?.RULES.TRIP_COST_MAX_PER_PERSON ?? DEFAULT_MAX_TRIP_COST_PER_PERSON;
  const tripCostMax = React.useMemo(
    () => maxTripCostPerPerson * (selectedSecondaryTravellerCount + 1),
    [maxTripCostPerPerson, selectedSecondaryTravellerCount],
  );

  return (
    <Controller
      name="totalTripCost"
      control={control}
      rules={{
        validate: {
          required: v => !!v || 'Please enter your trip cost.',
          max: v =>
            currency(v).value <= tripCostMax ||
            i18n.t('tripCost.tripCostMaxError', {
              totalMaxCost: currency(maxTripCostPerPerson, {
                symbol: '$',
                precision: 0,
              }).format(),
            }),
          min: v =>
            currency(v).value > tripCostMin ||
            i18n.t('tripCost.tripCostMinError', {
              totalMinCost: currency(minTripCostPerPerson, {
                symbol: '$',
                precision: 0,
              }).format(),
            }),
        },
      }}
      render={(props: { field: ControllerRenderProps<TripCostForm, 'totalTripCost'> }) => (
        <div>
          <Input
            id="trip-cost-input"
            rightIcon={buttonProps => {
              return (
                <Button
                  {...buttonProps}
                  isLoading={isPending}
                  title="Update"
                  variant={'primary'}
                  onClick={() => onSubmit(props.field.value, handleSubmit, prefixValue, setValue)}
                  disabled={isBoostUpdating}
                />
              );
            }}
            className="w-3/4 sm:w-1/2"
            errorMessage={errors.totalTripCost?.message && ' '}
            onChange={e => {
              if (e.target.value === prefixValue) {
                props.field.onChange('');
              } else {
                props.field.onChange(e.target.value);
              }
            }}
            onBlur={props.field.onBlur}
            value={
              props.field.value.length > 0
                ? prefixValue +
                  currency(props.field.value, {
                    symbol: '',
                    precision: 0,
                    separator: ',',
                  }).format()
                : ''
            }
          />
          {errors.totalTripCost?.message ? (
            <Text
              variant="footnote-12/r"
              className="text-error mt-s8 w-full text-left"
              {...testProps('text-input-error')}>
              {errors.totalTripCost?.message}
            </Text>
          ) : null}
        </div>
      )}
    />
  );
};
